/* eslint-disable local/ensure-wac-token-usage */
import { useCallback, useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { injectIntl, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    ufoEventConfirmationPageTemplateSelectionBack,
    ufoEventConfirmationPageTemplateSelectionContinue
} from "@/components/signup/utils";
import {
    TRACKING_EVENT_ACTION_SUBJECT_IDS,
    USER_SEARCH_PARAMS
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";

import {
    JiraTemplateChoice,
    useGupsStatus,
    useJiraTemplateChoice
} from "../../hooks";
import { useConfirmationPage } from "../../hooks/use-confirmation-page";
import { useScreenManager } from "../../hooks/use-screen-manager";
import { UserSegmentationTeamTypes } from "../../models/user-segmentation";
import { getCurrentUrlWithParams } from "../../utils/utils";
import { JiraOnboardingContainer } from "../jira-onboarding-container/jira-onboarding-container";

import Body from "./components/body";
import {
    TemplateOption,
    TemplateOptionMetadata,
    getTeamTypeTemplateOptions
} from "./team-type-template-map";

const buttonWrapperStyle = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    margin: "0 auto",
    padding: "36px 20px 20px 20px",
    border: "none",
    gap: token("space.250"),
    width: "372px",
    [media.below.sm]: {
        position: "fixed",
        insetInlineStart: 0,
        insetBlockEnd: "0",
        backgroundColor: "#FFF",
        padding: "14px 18px",
        width: "100%",
        zIndex: 1000,
        boxShadow: "-1px 0px 2px 0px #00000040"
    }
});

const buttonCssOverride = css({
    borderRadius: "40px !important",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "0 24px !important",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        fontFamily: "var(--bxp-charlie-text-font-family)",
        fontSize: "16px"
    },
    [media.above.sm]: {
        width: "min-content !important"
    }
});

const backButtonOverride = css({
    border: "2px solid #101214 !important",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& span"]: {
        [media.below.sm]: {
            position: "relative",
            insetBlockStart: "-2px"
        }
    },
    [media.above.sm]: {
        lineHeight: "33px !important"
    }
});

export const TemplateChoiceViewBase = ({
    contextSelectedTeamTypeState
}: {
    contextSelectedTeamTypeState: UserSegmentationTeamTypes;
}) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { addTemplateCallback } = useGupsStatus();

    //set these on submit
    const {
        setTemplateChoice,
        setTemplateChoiceId,
        setTemplateChoiceName,
        goBackToUserSegmentation,
        setTemplateMetadata
    } = useJiraTemplateChoice();

    const { prevScreen, nextScreen, prevScreenExists } = useScreenManager();

    const selectedTeamType = contextSelectedTeamTypeState;

    const doesPreviousScreenExist = prevScreenExists && prevScreenExists();

    const removeUrlParameter = useCallback((url: string, paramKey: string) => {
        const r = new URL(url);
        r.searchParams.delete(paramKey);
        return r.href;
    }, []);

    //set these on select
    const [selectedTemplate, setSelectedTemplate] =
        useState<JiraTemplateChoice>();
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
    const [selectedCardTitle, setSelectedCardTitle] = useState<string>();
    const [selectedTemplateMetadata, setSelectedTemplateMetadata] =
        useState<TemplateOptionMetadata>();

    // get template choices based on team type
    const teamTemplateChoices: TemplateOption[] = useMemo(
        () => getTeamTypeTemplateOptions(selectedTeamType, intl.formatMessage),
        [intl.formatMessage, selectedTeamType]
    );

    //set selected template to first template in array
    useEffect(() => {
        if (teamTemplateChoices) {
            setSelectedTemplateId(teamTemplateChoices[0]?.option.templateId);
            setSelectedTemplate(teamTemplateChoices[0]?.option.templateType);
            setSelectedTemplateMetadata(teamTemplateChoices[0]?.metadata);
            setSelectedCardTitle(teamTemplateChoices[0]?.option.title);
        }
    }, [teamTemplateChoices]);

    const messages = {
        header: intl.formatMessage({
            id: "TransparentTemplateChoice.header.latest",
            defaultMessage: "Select a template to get started"
        }),
        subheader: intl.formatMessage({
            id: "TransparentTemplateChoice.subheader.latest",
            defaultMessage: "Your choice won't limit what you can do in Jira"
        }),
        back: intl.formatMessage({
            id: "TransparentTemplateChoice.backButton",
            defaultMessage: "Back"
        }),
        continue: intl.formatMessage({
            id: "TransparentTemplateChoice.continueButton",
            defaultMessage: "Continue"
        }),
        transparentTemplateV2subHeader: intl.formatMessage({
            id: "TransparentTemplateV2.subheader",
            defaultMessage:
                "You can always change this later. Selecting a template won’t limit what you can do."
        })
    };

    return (
        <>
            <JiraOnboardingContainer
                header={messages.header}
                subheader={messages.transparentTemplateV2subHeader}
                hideFooterOnMobile={true}
            >
                <Body
                    setSelectedTemplate={setSelectedTemplate}
                    setSelectedTemplateId={setSelectedTemplateId}
                    setSelectedCardTitle={setSelectedCardTitle}
                    setSelectedTemplateMetadata={setSelectedTemplateMetadata}
                    selectedCardTitle={selectedCardTitle}
                    cards={teamTemplateChoices}
                    teamType={selectedTeamType}
                />
                <div css={buttonWrapperStyle}>
                    {doesPreviousScreenExist && (
                        <Button
                            shouldFitContainer
                            appearance="subtle"
                            css={[buttonCssOverride, backButtonOverride]}
                            onClick={() => {
                                try {
                                    ufoEventConfirmationPageTemplateSelectionBack.start();
                                    createAnalyticsEvent({
                                        eventType: "ui",
                                        action: "clicked",
                                        actionSubject: "button",
                                        actionSubjectId:
                                            TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceBack,
                                        attributes: {
                                            atlasTicket: "ATLAS-69623"
                                        }
                                    }).fire();
                                    const updatedUrl = removeUrlParameter(
                                        window.location.href,
                                        USER_SEARCH_PARAMS.teamType
                                    );
                                    history.replaceState({}, "", updatedUrl);
                                    goBackToUserSegmentation();
                                    prevScreen();
                                    ufoEventConfirmationPageTemplateSelectionBack.success();
                                } catch (e) {
                                    ufoEventConfirmationPageTemplateSelectionBack.failure(
                                        {
                                            error: e
                                        }
                                    );
                                }
                            }}
                        >
                            {messages.back}
                        </Button>
                    )}
                    <Button
                        css={buttonCssOverride}
                        shouldFitContainer
                        appearance="primary"
                        onClick={async () => {
                            try {
                                ufoEventConfirmationPageTemplateSelectionContinue.start();
                                ufoEventConfirmationPageTemplateSelectionContinue.setCustomData(
                                    {
                                        teamType: selectedTeamType,
                                        template: selectedTemplate,
                                        templateId: selectedTemplateId
                                    }
                                );
                                createAnalyticsEvent({
                                    eventType: "ui",
                                    action: "clicked",
                                    actionSubject: "button",
                                    actionSubjectId:
                                        TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceStartNow,
                                    attributes: {
                                        atlasTicket: "ATLAS-69623",
                                        template: selectedTemplate,
                                        teamType: selectedTeamType,
                                        templateId: selectedTemplateId
                                    }
                                }).fire();

                                // add templateChoice to query param to maintain selection on refresh
                                const updatedUrl = getCurrentUrlWithParams({
                                    templateId: selectedTemplateId || "",
                                    templateChoice: selectedTemplate || "",
                                    jtbd: selectedTemplateMetadata?.jtbd || ""
                                });
                                history.replaceState({}, "", updatedUrl);

                                if (selectedTemplateId) {
                                    await addTemplateCallback(
                                        selectedTemplateId
                                    );
                                }

                                setTemplateMetadata(selectedTemplateMetadata);
                                setTemplateChoice(selectedTemplate);
                                setTemplateChoiceId(selectedTemplateId);
                                setTemplateChoiceName(selectedCardTitle);

                                nextScreen();
                                ufoEventConfirmationPageTemplateSelectionContinue.success();
                            } catch (e) {
                                ufoEventConfirmationPageTemplateSelectionContinue.failure(
                                    {
                                        error: e
                                    }
                                );
                            }
                        }}
                    >
                        {messages.continue}
                    </Button>
                </div>
            </JiraOnboardingContainer>
        </>
    );
};

export const TemplateChoiceView = injectIntl(() => {
    //screen analytics event here
    const {
        userSegmentationContext: {
            selectedTeamType:
                contextSelectedTeamTypeState = "software-development"
        }
    } = useConfirmationPage();

    return (
        <TemplateChoiceViewBase
            contextSelectedTeamTypeState={contextSelectedTeamTypeState}
        />
    );
});
